import { Button, TextButton, Typography, useAlert } from "matsuri-ui"
import { css } from "@emotion/react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { EditExpenseDialog } from "./EditExpenseDialog"
import { TransportationExpenseList } from "./TransportationExpenseList"
import { TransportationExpenseListSkeleton } from "./TransportationExpenseListSkeleton"
import { CleanerPageHeader } from "@/parts/CleanerPageHeader"
import {
    TransportationExpense,
    displayDateWithYear,
    oldestVisibleExpenseDate
} from "@/model/transportationExpense"
import { DateString, formatDate } from "@/utils/dateString"
import { theme } from "@/theme"
import {
    transportationExpenseErrorMessages,
    useTransportationExpenseMutation,
    useTransportationExpensesForCleaner
} from "@/hooks/useTransportationExpense"
import { useTranslation } from "@/hooks/useTranslation"

export const TransportationExpensePage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { throwAlert } = useAlert()

    const today: DateString = formatDate(dayjs())
    const expenseDateFrom = oldestVisibleExpenseDate(today)

    const { data, error, isLoading, refetch } =
        useTransportationExpensesForCleaner({ expenseDateFrom })

    const [editingExpense, setEditingExpense] = useState<
        TransportationExpense | undefined
    >(undefined)
    const [submitting, setSubmitting] = useState(false)

    const { updateTransportationExpense } = useTransportationExpenseMutation()

    const handleUpdate = async (input: {
        amount: number
        routeDescription: string
    }) => {
        if (!editingExpense || submitting) {
            return
        }

        setSubmitting(true)
        try {
            const { error: updateError } = await updateTransportationExpense({
                transportationExpenseId: editingExpense.id,
                amount: input.amount,
                routeDescription: input.routeDescription
            })

            const errorType = updateError?.parse?.()?.errorType
            if (errorType === "transportation_expense_not_editable") {
                // 修正期限を過ぎている場合は一覧を再取得してボタン表示を同期する
                refetch()
                setEditingExpense(undefined)
            }

            throwAlert(updateError, {
                errorMessage: {
                    happend: t(
                        (errorType &&
                            transportationExpenseErrorMessages[errorType]) ||
                            "申請内容の修正に失敗しました"
                    )
                },
                successMessage: t("申請内容を修正しました"),
                onSuccess: () => {
                    setEditingExpense(undefined)
                    refetch()
                }
            })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div
            css={css`
                min-height: 100vh;
                background-color: ${
                    theme.palette.intentions.cleanerBackground.main
                };
            `}
        >
            <CleanerPageHeader title={t("交通費申請")} />

            <div
                css={css`
                    display: grid;
                    gap: 24px;
                    padding: 16px 20px;
                `}
            >
                <Button
                    variant="filled"
                    color="primary"
                    onClick={() => navigate("/transportation_expenses/new")}
                >
                    {t("新規申請")}
                </Button>

                <div
                    css={css`
                        display: grid;
                        gap: 12px;
                    `}
                >
                    <Typography variant="h4">{t("申請履歴")}</Typography>
                    <Typography
                        variant="caption"
                        css={css`
                            color: ${theme.palette.gray[500]};
                        `}
                    >
                        {/* 1月に「12/1以降」と出すと当年の12月に見えるため、年も添える */}
                        {t("{{date}}以降の申請を表示しています", {
                            params: {
                                date: displayDateWithYear(expenseDateFrom)
                            }
                        })}
                    </Typography>

                    {isLoading ? (
                        <TransportationExpenseListSkeleton />
                    ) : error ? (
                        <div
                            css={css`
                                display: grid;
                                gap: 4px;
                                justify-items: start;
                            `}
                        >
                            <Typography
                                css={css`
                                    color: ${
                                        theme.palette.intentions.secondary.main
                                    };
                                `}
                            >
                                {t("申請履歴の取得に失敗しました")}
                            </Typography>
                            <TextButton
                                color="primary"
                                onClick={() => refetch()}
                            >
                                {t("再読み込み")}
                            </TextButton>
                        </div>
                    ) : (
                        <TransportationExpenseList
                            expenses={data?.transportationExpenses ?? []}
                            onEdit={setEditingExpense}
                        />
                    )}
                </div>
            </div>

            {editingExpense && (
                <EditExpenseDialog
                    expense={editingExpense}
                    submitting={submitting}
                    onSubmit={handleUpdate}
                    onClose={() => setEditingExpense(undefined)}
                />
            )}
        </div>
    )
}
